import { BadgeWithIcon } from '@orn/shared-web/components/Badge';
import { useResizeObserver } from '@orn/shared-web/hooks/useResizeObserver';
import { CheckIcon, EyeIcon, PasscodeLockIcon, PlusIcon, ShieldTickIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import onboardingInstitutionsIcons from '../../../assets/onboarding-institutions-icons.png';
import plaidLogo from '../../../assets/plaid-logo.svg';
import { useDependants } from '../../../hooks/queries/useDependants';
import { useFinancialAccounts } from '../../../hooks/queries/useFinancialAccounts';
import { groupAccountsBySubtype, subtypeToDisplayName, supportedSubtypes } from '../../../utils/accounts';
import AccountMetadata from '../../AccountMetadata/AccountMetadata';
import Entity from '../../entity';
import { OnboardingTitle } from '../../OnboardingLayout';
import PlaidLinkButton from '../../plaid/PlaidLinkButton/PlaidLinkButton';

import { getDependantPossessive } from './dependantUtils';
import OnboardingStepActions from './OnboardingStepActions';

import type { FinancialAccount } from '../../../hooks/queries/useFinancialAccounts';
import type { IOnboardingStepProps } from '../types';
import type { FunctionComponent, ReactNode, SVGProps } from 'react';

const SUPPORTED_BANKS_COUNT_LABEL = 'Over 5,000+ banks supported';

/** Hide the bottom fade once the list is within this many px of the end — covers both a short list and a scrolled-to-bottom long list. */
const SCROLL_FADE_HIDE_THRESHOLD_PX = 5;

interface ITrustRowProps {
  Icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  children: ReactNode;
}

const TrustRow = ({ Icon, children }: ITrustRowProps) => (
  <div className="flex items-start gap-2">
    <Icon className="text-secondary mt-px size-4 shrink-0" />
    <p className="text-secondary text-xs leading-[18px]">{children}</p>
  </div>
);

/**
 * Pre-connection view: explains Plaid + bank coverage and hands off to
 * {@link PlaidLinkButton}. No `onSuccess` is wired here — once Plaid reports
 * success the central Apollo cache update refreshes `GetAllFinancialAccounts`,
 * so the parent re-renders straight into {@link ConnectedAccountsContent}.
 *
 * The subtitle and credentials trust row name the dependant(s) ("Jane and
 * Peter's accounts"), falling back to "your loved one's" before the dependants
 * query resolves.
 */
const ConnectAccountsContent = ({ onBack }: { onBack?: () => void }) => {
  const { dependants } = useDependants();
  const possessive = getDependantPossessive(dependants, "your loved one's");

  return (
    <>
      <OnboardingTitle
        title={
          <>
            Connect your accounts securely with{' '}
            <img src={plaidLogo} alt="Plaid" className="inline-block h-8 align-middle" />
          </>
        }
        subtitle={`We'll securely connect ${possessive} accounts to provide real-time monitoring and alerts.`}
        onBack={onBack}
      />

      <div className="bg-utility-stone-100 border-utility-stone-200 shadow-xs flex w-full flex-col gap-3 rounded-xl border px-6 py-4">
        <p className="text-secondary text-sm font-bold">{SUPPORTED_BANKS_COUNT_LABEL}</p>
        <div className="w-full overflow-hidden">
          <img
            src={onboardingInstitutionsIcons}
            alt="Supported bank logos"
            className="block w-full"
            style={{
              maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
            }}
          />
        </div>

        <div className="bg-utility-mist-100 border-utility-mist-500 flex w-full flex-col gap-[11px] rounded-[11px] border p-4">
          <TrustRow Icon={ShieldTickIcon}>
            Powered by Plaid, the industry standard for secure financial connections
          </TrustRow>
          <TrustRow Icon={EyeIcon}>We only access read-only data — we can never move money</TrustRow>
          <TrustRow
            Icon={PasscodeLockIcon}
          >{`You'll need ${possessive} login credentials for their accounts`}</TrustRow>
        </div>
      </div>

      <PlaidLinkButton bankConnectionId={null} size="xl" color="primary" className="w-full">
        Connect with Plaid
      </PlaidLinkButton>
    </>
  );
};

/**
 * Post-connection view: lists the linked accounts grouped by subtype, lets the
 * caregiver add more via Plaid, and advances the wizard through "Continue".
 */
const ConnectedAccountsContent = ({
  financialAccounts,
  onNext,
  onBack,
}: {
  financialAccounts: FinancialAccount[];
  onNext: () => void;
  onBack?: () => void;
}) => {
  const groupedAccounts = React.useMemo(() => groupAccountsBySubtype(financialAccounts), [financialAccounts]);

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [showBottomFade, setShowBottomFade] = React.useState(false);

  const updateBottomFade = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowBottomFade(distanceFromBottom > SCROLL_FADE_HIDE_THRESHOLD_PX);
  }, []);

  // The list can grow past the height cap without the container itself resizing, so recompute on content change too.
  React.useEffect(() => {
    updateBottomFade();
  }, [updateBottomFade, financialAccounts]);

  // Responsive width changes re-wrap rows and shift the scrollable height.
  useResizeObserver({ ref: scrollRef, onResize: updateBottomFade });

  return (
    <>
      <OnboardingTitle
        title="Connect your accounts"
        subtitle="Add accounts and view the data you're sharing."
        onBack={onBack}
      />

      <div className="relative w-full">
        {/* max-h matches the Figma card height (node 5323:115087); list scrolls past it. */}
        <div
          ref={scrollRef}
          onScroll={updateBottomFade}
          className="bg-utility-stone-100 border-utility-stone-200 shadow-xs scrollbar-visible flex max-h-[437px] w-full flex-col gap-6 overflow-y-auto rounded-md border py-4 pl-4 pr-6"
        >
          {supportedSubtypes.map((subtype) => {
            const accounts = groupedAccounts[subtype];
            if (!accounts || accounts.length === 0) {
              return null;
            }

            return (
              <div key={subtype} className="flex w-full flex-col gap-2">
                <p className="text-primary text-md font-semibold">{subtypeToDisplayName(subtype)}</p>
                {accounts.map((account) => (
                  <div
                    key={account.id}
                    className="bg-secondary_subtle border-secondary flex w-full items-center justify-between gap-4 rounded-xl border p-3"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-4">
                      <Entity.Avatar entity={account} size="lg" />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <p className="text-primary text-md truncate font-semibold">{account.name}</p>
                        <AccountMetadata className="text-sm" bankName={account.bank.name} mask={account.mask} />
                      </div>
                    </div>
                    <BadgeWithIcon color="success" size="sm" type="color" iconLeading={CheckIcon}>
                      Connected
                    </BadgeWithIcon>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div
          className={cx(
            'from-utility-stone-100 pointer-events-none absolute inset-x-px bottom-px h-[57px] rounded-b-md bg-gradient-to-t to-transparent transition-opacity duration-200',
            showBottomFade ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>

      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full justify-center">
          <PlaidLinkButton bankConnectionId={null} size="lg" color="link-color" iconLeading={PlusIcon}>
            Add another account
          </PlaidLinkButton>
        </div>
        <OnboardingStepActions continueLabel="Continue" onContinue={onNext} />
      </div>
    </>
  );
};

interface IBaseFinancialAccountConnectStepProps extends IOnboardingStepProps {
  financialAccounts: FinancialAccount[];
}

/**
 * Presentational onboarding step for connecting a loved one's financial
 * accounts. Changes shape based on whether any account is linked yet:
 *
 * - **No accounts** → {@link ConnectAccountsContent}: Plaid trust card + CTA.
 * - **One or more** → {@link ConnectedAccountsContent}: the grouped account
 *   list with "Continue" advancing the wizard to the pricing screen.
 *
 * The Plaid token exchange lives inside {@link PlaidLinkButton}; this component
 * only reacts to the resulting account list. Split from the data-fetching
 * wrapper so Storybook can render both shapes from plain props.
 *
 * Figma: Mantle - UX/UI, node 4176:83536 (connect) and 5323:115087 (connected).
 */
export const BaseFinancialAccountConnectStep = ({
  onNext,
  onBack,
  financialAccounts,
}: IBaseFinancialAccountConnectStepProps) => {
  const hasAccounts = financialAccounts.length > 0;

  return (
    <div className="flex w-full flex-col gap-6">
      {hasAccounts ? (
        <ConnectedAccountsContent financialAccounts={financialAccounts} onNext={onNext} onBack={onBack} />
      ) : (
        <ConnectAccountsContent onBack={onBack} />
      )}
    </div>
  );
};

BaseFinancialAccountConnectStep.displayName = 'BaseFinancialAccountConnectStep';

/**
 * Data wrapper for {@link BaseFinancialAccountConnectStep}. Reads the linked
 * accounts so the step renders the connect prompt or the connected-accounts
 * list; the list refreshes automatically after a Plaid connection via the
 * central Apollo cache update.
 */
const FinancialAccountConnectStep = (props: IOnboardingStepProps) => {
  const { financialAccounts } = useFinancialAccounts();

  return <BaseFinancialAccountConnectStep financialAccounts={financialAccounts} {...props} />;
};

FinancialAccountConnectStep.displayName = 'FinancialAccountConnectStep';

export default FinancialAccountConnectStep;
